import { FWSSP_PROBE, fwsspIsTarget, safeKey } from "./config";
import { bump, pushSample } from "./sink";

const PARAM_KEYS = [
  "nw", "resp", "prof", "csid", "caid", "mode", "flag",
  "slid", "slau", "tpos", "ptgt", "mind", "maxd",
  "_fw_player_width", "_fw_player_height", "_fw_video_context", "_fw_plcmt_type",
];

const SLOW_MS = 1500;

function now(): number {
  try { if (typeof performance !== "undefined" && performance.now) return performance.now(); } catch { /* no-op */ }
  return Date.now();
}

function bucket(ms: number): string {
  if (ms < 500) return "lt500";
  if (ms < 1000) return "500_1000";
  if (ms < 1500) return "1000_1500";
  if (ms < 2000) return "1500_2000";
  if (ms < 3000) return "2000_3000";
  return "gte3000";
}

function extractParams(url: string): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const q = url.indexOf("?");
    const qs = q === -1 ? "" : url.slice(q + 1);
    qs.split(/[;&]/).forEach((pair) => {
      const eq = pair.indexOf("=");
      if (eq === -1) return;
      const k = pair.slice(0, eq);
      if (PARAM_KEYS.indexOf(k) !== -1 && !(k in out)) {
        try { out[k] = decodeURIComponent(pair.slice(eq + 1)); } catch { out[k] = pair.slice(eq + 1); }
      }
    });
  } catch { /* no-op */ }
  return out;
}

function getTiming(url: string): any {
  try {
    if (typeof performance === "undefined" || !performance.getEntriesByName) return null;
    const entries = performance.getEntriesByName(url);
    if (!entries || !entries.length) return null;
    const e: any = entries[entries.length - 1];
    const tao = e.requestStart > 0 && e.responseStart > 0;
    const r = (n: number) => (n > 0 ? Math.round(n) : 0);
    return {
      tao,
      dns: r(e.domainLookupEnd - e.domainLookupStart),
      tcp: r(e.connectEnd - e.connectStart),
      tls: e.secureConnectionStart > 0 ? r(e.connectEnd - e.secureConnectionStart) : 0,
      ttfb: tao ? r(e.responseStart - e.requestStart) : 0,
      download: tao ? r(e.responseEnd - e.responseStart) : 0,
      total: r(e.responseEnd - e.startTime),
      protocol: e.nextHopProtocol || "",
      transferSize: e.transferSize || 0,
    };
  } catch { return null; }
}

function classify(status: number, errName: string): string {
  if (status >= 400) return `http_${status}`;
  if (status === 0) return errName === "AbortError" ? "abort" : "neterror";
  return `http_${status}`;
}

function isError(code: string): boolean {
  return code === "abort" || code === "neterror" || /^http_(4|5)\d\d$/.test(code);
}

function analyze(
  method: string, url: string, status: number, statusText: string,
  body: string, headers: string, elapsedMs: number, errName: string, errMsg: string,
): void {
  if (Math.random() > FWSSP_PROBE.sampleRate) return;

  const params = extractParams(url);
  const nw = safeKey(params.nw);
  const code = classify(status, errName);
  const ms = Math.round(elapsedMs);
  const timing = getTiming(url);

  bump(`${nw}/req`);
  bump(`${nw}/${code}`);

  bump(`timing/total_${bucket(ms)}`);
  if (timing) {
    bump(`timing/tao_${timing.tao ? "yes" : "no"}`);
    if (timing.tao) {
      bump(`timing/ttfb_${bucket(timing.ttfb)}`);
      bump(`timing/conn_${bucket(timing.dns + timing.tcp)}`);
    }
  }

  if (isError(code)) {
    bump("errors/total");
    bump(`errors/${code}`);
    if (code === "abort") bump(ms >= 1800 ? "errors/abort_near_timeout" : "errors/abort_early");

    pushSample("_errors", {
      ts: new Date().toISOString(),
      nw, code, elapsed_ms: ms, timing,
      status, statusText: statusText || "", reason: errMsg || errName || "",
      method: method || "GET", params,
      url: url.slice(0, 2000),
      headers: (headers || "").slice(0, 2000),
      body: (body || "").slice(0, 4000),
    }, 50);
    return;
  }

  const hasAd = /<Ad[\s>]/.test(body || "");
  const hasSticky = (body || "").indexOf("StickyPricing") !== -1;
  bump(`${nw}/${hasAd ? "has_ad" : "empty"}`);
  if (hasAd) {
    bump(`fill/${hasSticky ? "has_sticky" : "ad_no_price"}`);
    bump(`${nw}/${hasSticky ? "has_sticky" : "ad_no_price"}`);
  } else {
    bump("fill/empty");
  }

  if (hasAd && !hasSticky) {
    pushSample("_noprice", {
      ts: new Date().toISOString(),
      nw, elapsed_ms: ms, params,
      url: url.slice(0, 500),
      vast: (body || "").slice(0, 20000),
    }, 30);
  }

  if (ms >= SLOW_MS) {
    pushSample("_slow", {
      ts: new Date().toISOString(),
      nw, code, elapsed_ms: ms, timing,
      method: method || "GET", params,
      url: url.slice(0, 2000),
    }, 50);
  }
}

export function installNetworkProbe(): void {
  const w = window as any;

  try { if (typeof performance !== "undefined" && performance.setResourceTimingBufferSize) performance.setResourceTimingBufferSize(500); } catch { /* no-op */ }

  const origFetch = w.fetch;
  if (typeof origFetch === "function") {
    w.fetch = function (this: any, ...args: any[]) {
      const req = args[0];
      const url = typeof req === "string" ? req : (req && req.url) || "";
      const method = (args[1] && args[1].method) || (req && req.method) || "GET";
      const t0 = now();
      const p = origFetch.apply(this, args);
      if (fwsspIsTarget(url)) {
        p.then((resp: any) => {
          try {
            let hdr = "";
            try { resp.headers.forEach((v: string, k: string) => { hdr += `${k}: ${v}\n`; }); } catch { /* no-op */ }
            resp.clone().text()
              .then((t: string) => analyze(method, url, resp.status, resp.statusText, t, hdr, now() - t0, "", ""))
              .catch(() => { /* no-op */ });
          } catch { /* no-op */ }
        }).catch((err: any) => {
          analyze(method, url, 0, "", "", "", now() - t0, (err && err.name) || "", String((err && err.message) || err));
        });
      }
      return p;
    };
  }

  const XHR = w.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const origOpen = XHR.prototype.open;
    const origSend = XHR.prototype.send;
    XHR.prototype.open = function (this: any, method: string, url: string, ...rest: any[]) {
      this.__fwUrl = url;
      this.__fwMethod = method;
      return origOpen.call(this, method, url, ...rest);
    };
    XHR.prototype.send = function (this: any, ...sendArgs: any[]) {
      const url = this.__fwUrl || "";
      if (fwsspIsTarget(url)) {
        const t0 = now();
        this.addEventListener("loadend", () => {
          try {
            let hdr = "";
            try { hdr = this.getAllResponseHeaders() || ""; } catch { /* no-op */ }
            const errName = this.status === 0 ? (this.__fwAborted ? "AbortError" : "") : "";
            analyze(this.__fwMethod || "GET", url, this.status, this.statusText, this.responseText, hdr, now() - t0, errName, "");
          } catch { /* no-op */ }
        });
        this.addEventListener("abort", () => { this.__fwAborted = true; });
      }
      return origSend.apply(this, sendArgs);
    };
  }
}
